import { Input, Textarea } from '@/components/ui/FormControls';
import ComboSelect from '@/components/common/ComboSelect';
import { useRef, useState } from 'react';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useOperations, useOperation } from '@/hooks/useOperations';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import { operationsRpc } from '@/lib/operations';
import WorkModalShell from '@/components/common/WorkModalShell';
import { catalogInput as input } from '@/components/products/CatalogFields';
type Choice = { id: string; sku: string; name: string; default_cost: number };
export default function StockEntry() {
  const { can, organization, profile } = useAuth();
  const client = useQueryClient();
  const ops = useOperations();
  const [open, setOpen] = useState(false),
    [variant, setVariant] = useState(''),
    [qty, setQty] = useState(''),
    [cost, setCost] = useState(''),
    [reason, setReason] = useState(''),
    [error, setError] = useState('');
  const lock = useRef(false);
  const intent = useOperationIntent('inventory-stock-entry');
  const save = useOperation('add_stock_entry_v1');
  const choices = useQuery({
    queryKey: ['stock_entry_choices', organization?.id, profile?.id],
    enabled: open && ops.data?.modules.catalog === 1,
    queryFn: async () => {
      const all: Choice[] = [];
      for (let offset = 0; ; offset += 500) {
        const rows = await operationsRpc<Choice[]>('list_stock_variants_v1', { p_offset: offset });
        all.push(...rows);
        if (rows.length < 500) return all;
      }
    },
  });
  if (!can('inventory.opening') || ops.data?.modules.catalog !== 1) return null;
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lock.current) return;
    setError('');
    if (
      !variant ||
      !Number.isInteger(Number(qty)) ||
      Number(qty) <= 0 ||
      cost === '' ||
      !Number.isFinite(Number(cost)) ||
      Number(cost) < 0 ||
      reason.trim().length < 3
    ) {
      setError(
        'Select a variant, enter a positive whole quantity, a unit cost and a reason of at least 3 characters.',
      );
      return;
    }
    lock.current = true;
    try {
      await save.mutateAsync({
        variant_id: variant,
        quantity: Number(qty),
        unit_cost: Number(cost),
        reason: reason.trim(),
        request_key: intent.key('add'),
      });
      intent.complete('add');
      setOpen(false);
      setVariant('');
      setQty('');
      setCost('');
      setReason('');
      toast.success('Stock added. No purchase or account debit was created.');
    } catch (x) {
      setError((x as Error).message);
    } finally {
      lock.current = false;
    }
  }
  return (
    <>
      <button
        className="min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
        onClick={() => {
          setError('');
          setOpen(true);
        }}
      >
        + Add stock
      </button>
      {open && (
        <WorkModalShell open title="Add stock" onClose={() => setOpen(false)} busy={save.isPending}>
          <h2 className="shrink-0 text-lg font-semibold">Add stock</h2>
          <form onSubmit={submit} className="flex min-h-0 flex-col">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
              <p className="text-sm text-muted-foreground">
                Record stock received today. Purchase records and bank accounts are managed
                separately.
              </p>
              <label className="block space-y-1 text-sm">
                Product / variant *
                <ComboSelect
                  required
                  className={input}
                  value={variant}
                  disabled={choices.isLoading || save.isPending}
                  onChange={(value) => {
                    setVariant(value || '');
                    const selected = choices.data?.find((v) => v.id === value);
                    setCost(selected ? String(selected.default_cost) : '');
                  }}
                  helpLabel="Product / variant *"
                  placeholder={choices.isLoading ? 'Loading variants…' : 'Search product code, SKU or name'}
                  searchPlaceholder="Product code, SKU or name…"
                  options={(choices.data || []).map((v) => ({ value: v.id, label: v.name, sublabel: v.sku }))}
                />
              </label>
              {choices.error && (
                <p role="alert" className="text-sm text-destructive">
                  {choices.error.message}
                  <button
                    type="button"
                    onClick={() => void choices.refetch()}
                    className="min-h-11 px-2 underline"
                  >
                    Retry
                  </button>
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm">
                  Quantity *
                  <Input
                    required
                    className={input}
                    type="number"
                    min={1}
                    step={1}
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    helpLabel="Quantity *"
                  />
                </label>
                <label className="text-sm">
                  Unit cost (₹) *
                  <Input
                    required
                    className={input}
                    type="number"
                    min={0}
                    step=".01"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    helpLabel="Unit cost (₹) *"
                  />
                </label>
              </div>
              <label className="block space-y-1 text-sm">
                Reason *
                <Textarea
                  required
                  minLength={3}
                  maxLength={500}
                  className={input}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  helpLabel="Reason *"
                />
              </label>
              {error && (
                <div role="alert" className="text-sm text-destructive">
                  <p>{error}</p>
                  <button
                    type="button"
                    className="min-h-11 underline"
                    onClick={() =>
                      void intent
                        .recover('add')
                        .then((r) => {
                          if (r) {
                            setOpen(false);
                            setVariant('');
                            setQty('');
                            setCost('');
                            setReason('');
                            void refreshSaleConsumers(client);
                            toast.success('Previous stock entry was completed.');
                          } else setError('No completed stock entry found. Review and retry.');
                        })
                        .catch((e) => setError(e.message))
                    }
                  >
                    Recover previous entry
                  </button>
                </div>
              )}
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-border pt-3">
              <button
                type="button"
                className="min-h-11 px-4 text-sm"
                disabled={save.isPending}
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button
                className="min-h-11 rounded-xl bg-primary px-5 text-sm text-primary-foreground disabled:opacity-50"
                disabled={save.isPending || !choices.data?.length}
              >
                {save.isPending ? 'Saving…' : 'Add stock'}
              </button>
            </div>
          </form>
        </WorkModalShell>
      )}
    </>
  );
}
